import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { FocusableElement } from "@chakra-ui/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import useOffsetCodeConfigs from "features/fees/offsets/useOffsetCodeConfigs";
import { useFeeOffsettableExpenseCreate } from "gen/endpoints/fee-offsettable-expense/fee-offsettable-expense";
import { useFundList } from "gen/endpoints/fund-entities/fund-entities";
import { FeeOffsettableExpenseCreateRequestDataRequest } from "gen/model/feeOffsettableExpenseCreateRequestDataRequest";
import { feeOffsettableExpenseCreateBody } from "gen/schemas/fee-offsettable-expense/fee-offsettable-expense";
import React, { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import Form from "shared/components/form/Form";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { useBoolean } from "usehooks-ts";

enum FundFamilyOrSpecificEntitiesEnum {
  fund_family = "fund_family",
  specific_entities = "specific_entities",
}

const FeeOffsettableExpenseCreateModal: React.FC<{
  fundFamilyId: string;
  onCloseComplete: () => void;
  onSuccess: () => void;
}> = ({ fundFamilyId, onCloseComplete, onSuccess }) => {
  const { t } = useTranslation(["fees", "fundFamily"]);

  const offsetCodeRef = useRef<FocusableElement>(null);

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const {
    value: isFundEntityMultiSelectOpen,
    setTrue: showFundEntityMultiSelect,
    setFalse: hideFundEntityMultiSelect,
  } = useBoolean(false);

  const { data: transactionCodeConfigs } = useOffsetCodeConfigs(fundFamilyId);

  const { data: funds } = useFundList({
    fund_family_ids: [fundFamilyId],
  });

  const { mutateAsync: createFeeOffsettableExpense } =
    useFeeOffsettableExpenseCreate();

  const form = useForm<
    FeeOffsettableExpenseCreateRequestDataRequest & {
      allocation_type: FundFamilyOrSpecificEntitiesEnum;
    }
  >({
    resolver: zodResolver(feeOffsettableExpenseCreateBody),
    defaultValues: {
      allocation_type: FundFamilyOrSpecificEntitiesEnum.fund_family,
    },
  });

  const allocationType = useWatch({
    control: form.control,
    name: "allocation_type",
  });
  useEffect(() => {
    switch (allocationType) {
      case FundFamilyOrSpecificEntitiesEnum.fund_family:
        hideFundEntityMultiSelect();
        break;
      case FundFamilyOrSpecificEntitiesEnum.specific_entities:
        form.setValue("fund_entities_affected_ids", []);
        showFundEntityMultiSelect();
        break;
      default:
        form.setValue("fund_entities_affected_ids", []);
    }
  }, [
    allocationType,
    form,
    hideFundEntityMultiSelect,
    showFundEntityMultiSelect,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      initialFocusRef={offsetCodeRef}
      scrollBehavior="inside"
    >
      <Form
        form={form}
        onSubmit={(data) => createFeeOffsettableExpense({ fundFamilyId, data })}
        onSuccess={() => {
          onSuccess();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("fees:offsets.add.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormSelect
              ref={offsetCodeRef}
              title={t("fees:offsets.add.offsetCode")}
              name="transaction_code_config_id"
            >
              {transactionCodeConfigs?.map((config) => (
                <SelectOption key={config.id} value={config.id!}>
                  {config.name}
                </SelectOption>
              ))}
            </FormSelect>
            <FormMoneyInput name="amount" title={t("fees:offsets.amount")} />
            <FormToggleButtonGroup name="allocation_type">
              <ToggleButton
                value={FundFamilyOrSpecificEntitiesEnum.fund_family}
              >
                {t("fees:offsets.fundFamily")}
              </ToggleButton>
              <ToggleButton
                value={FundFamilyOrSpecificEntitiesEnum.specific_entities}
              >
                {t("fees:offsets.specificEntities")}
              </ToggleButton>
            </FormToggleButtonGroup>
            {isFundEntityMultiSelectOpen ? (
              <FormSelect
                isMulti={true}
                title={t("fees:offsets.entitiesApplied")}
                name="fund_entities_affected_ids"
              >
                {funds?.map((fund) => (
                  <SelectOption key={`fund_entity_${fund.id}`} value={fund.id}>
                    {fund.legal_name}
                  </SelectOption>
                ))}
              </FormSelect>
            ) : (
              <FormHiddenInput
                name="fund_entities_affected_ids"
                value={funds?.map((fund) => fund.id) ?? []}
              />
            )}
            <FormTextArea name="notes" title={t("fees:offsets.notes")} />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} />}
              confirm={
                <FormSubmitButton>
                  {t("fees:offsets.add.addOffset")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};

export default FeeOffsettableExpenseCreateModal;
